import { useQuery } from "@tanstack/react-query";
import { userApi } from "@bw/shared/api";
import type { BookingResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * Reservations the caller hosts or currently participates in across ALL
 * facilities (GET /me/bookings).
 *
 * The backend scopes the list to the token user server-side and returns it
 * ordered by `(start_time, booking_id)` — there is no client-side identity filter
 * to forge and no client-side sort to invent. Accepted-participant rows keep the
 * host identity but redact host-owned money. This is the CROSS-FACILITY companion
 * to the facility-scoped `useMyBookings`; the two own distinct query keys so
 * neither evicts the other from cache.
 *
 * Each row's facility is identified only by `facility_id` on the response (the
 * booking carries no facility NAME); the page resolves names separately via the
 * real facility endpoint — never fabricated here.
 */
export function useAllBookings(enabled = true) {
  // `enabled` (default true) lets a PUBLIC-BROWSE caller gate the fetch on auth
  // state. The player home renders for an anonymous visitor too, so the HomePage
  // passes `Boolean(user)` — this token-scoped `/me/bookings` read must NOT fire
  // when logged out (no 401 spam; the home shows a sign-in CTA instead). The
  // guarded /me/bookings calendar leaves the default `true`.
  return useQuery<BookingResponse[]>({
    queryKey: queryKeys.booking.allBookings(),
    queryFn: () => userApi.listMyBookings(),
    enabled,
  });
}
